import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PartnerCard from '../components/ConsPartnerCard';
import PropertyCard from '../components/PropertyCard';
import StatsSection from '../components/ui/StatsSection';
import { usePropiedades } from '../components/PropiedadesContext';
import { LocationType } from '../components/LocationSuggestions';
import EnhancedLocationSearch from '../components/EnhancedLocationSearch';
import {
  ChevronLeft, ChevronRight, AlertCircle,
  Home as HomeIcon, Building, Map, Store,
  TrendingUp, ArrowRight, Star, Award,
  Users, Shield, CheckCircle, Sparkles,
  Target, Globe, Zap, Search, Filter, 
  MapPin, Building2, Map as MapIcon,
  X, ChevronDown, Check, Castle
} from 'lucide-react';

// Configuration and constants
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';

const propertyTypeMap = {
  casa: {
    route: 'propiedades/casas',
    label: 'Casas'
  },
  departamento: {
    route: 'propiedades/departamentos',
    label: 'Departamentos'
  },
  terreno: {
    route: 'propiedades/terrenos',
    label: 'Terrenos'
  },
  local: {
    route: 'propiedades/locales',
    label: 'Locales Comerciales'
  }
};

const PROPERTY_TYPES = [
  { value: 'todos', label: 'Todos los tipos', icon: Search },
  { value: 'casa', label: 'Casa', icon: HomeIcon },
  { value: 'departamento', label: 'Departamento', icon: Building2 },
  { value: 'terreno', label: 'Terreno', icon: MapIcon },
  { value: 'local', label: 'Local Comercial', icon: Store },
  { value: 'oficina', label: 'Oficina', icon: Building }
];

const PROJECT_TYPES = [
  { value: 'todos', label: 'Todos los tipos', icon: Search },
  { value: 'residencial', label: 'Residencial', icon: Building2 },
  { value: 'comercial', label: 'Comercial', icon: Store },
  { value: 'mixto', label: 'Mixto', icon: Building }
];

const PROJECT_STAGES = [
  { value: 'todos', label: 'Todas las etapas' },
  { value: 'planos', label: 'En planos' },
  { value: 'lanzamiento', label: 'Lanzamiento' },
  { value: 'construccion', label: 'En construcción' },
  { value: 'terminado', label: 'Terminado' }
];

// Enhanced Animation Variants
const animations = {
  fadeInUp: {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      } 
    }
  },
  
  staggerContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  },
  
  cardHover: {
    rest: { 
      scale: 1,
      y: 0,
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
    },
    hover: {
      scale: 1.02,
      y: -4,
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08)",
      transition: { 
        duration: 0.3,
        ease: "easeOut"
      }
    }
  },

  slideIn: {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4 }
    }
  }
};

// Enhanced Tab Button Component
const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <motion.button
    onClick={onClick}
    className={`group relative overflow-hidden px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-lg sm:rounded-xl
              transition-all duration-300 ${
      active 
        ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-md' 
        : 'text-white/80 dark:text-gray-300 hover:text-white dark:hover:text-white hover:bg-white/10 dark:hover:bg-white/10'
    }`}
    whileHover={{ scale: 1.01, y: -0.5 }}
    whileTap={{ scale: 0.99 }}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                    translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
    <span className="relative z-10 flex items-center gap-1 sm:gap-1.5">
      {Icon && <Icon className="hidden sm:block w-3.5 h-3.5" />}
      {children}
    </span>
  </motion.button>
);

// Enhanced Property Type Button Component
const PropertyTypeButton = ({ icon: Icon, label, selected, onClick }) => (
  <motion.button
    onClick={onClick}
    className={`group relative overflow-hidden flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300
              ${selected 
      ? 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white shadow-md' 
      : 'bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700'}`}
    whileHover={{ scale: 1.01, y: -1 }}
    whileTap={{ scale: 0.99 }}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                    translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
    <Icon className={`hidden sm:block w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300 relative z-10
                   ${selected ? 'text-white' : 'text-blue-500 dark:text-blue-400 group-hover:scale-105'}`} />
    <span className="font-medium relative z-10 text-sm">{label}</span>
    {selected && <Sparkles className="hidden sm:block w-3 h-3 text-white/70 ml-auto relative z-10" />}
  </motion.button>
);

// Enhanced Filter Input Component
const FilterInput = ({ label, value, onChange, placeholder, type = "text", min, icon: Icon }) => (
  <div className="space-y-2">
    <label className="block text-xs font-medium text-white/90 dark:text-gray-300 flex items-center gap-1.5">
      {Icon && <Icon className="hidden sm:block w-3.5 h-3.5" />}
      {label}
    </label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        className="w-full px-3 sm:px-3.5 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 placeholder-white/50 dark:placeholder-gray-400 text-sm
                 border border-white/20 dark:border-white/10 focus:outline-none focus:ring-3 focus:ring-blue-500/30 
                 focus:border-blue-400/50 dark:focus:border-blue-400/50 transition-all duration-300 font-medium"
      />
      <div className="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 
                      opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  </div>
);

// Enhanced Components
const ProfessionalSectionTitle = ({ icon: Icon, title, subtitle, accent = false }) => (
  <motion.div
    className="text-center mb-8 sm:mb-12 px-4"
    variants={animations.fadeInUp}
  >
    <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-2xl mb-4 sm:mb-5 ${
      accent 
        ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/20' 
        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 text-gray-700 dark:text-gray-300 shadow-md'
    }`}>
      <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
    </div>
    
    <motion.h2 
      className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 sm:mb-3 tracking-tight px-2"
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      viewport={{ once: true }}
    >
      {title}
    </motion.h2>
    
    {subtitle && (
      <motion.p 
        className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed"
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        viewport={{ once: true }}
      >
        {subtitle}
      </motion.p>
    )}
  </motion.div>
);

const NavigationButton = ({ onClick, disabled, direction, variant = 'default' }) => {
  const baseClasses = "relative p-3 rounded-xl transition-all duration-200 border";
  const variants = {
    default: disabled 
      ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed' 
      : 'backdrop-blur-xl bg-white/80 dark:bg-white/10 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-white/20 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/20 hover:text-blue-600 dark:hover:text-blue-400 shadow-md hover:shadow-lg',
    primary: disabled
      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
      : 'bg-blue-600 dark:bg-blue-500 text-white border-blue-600 dark:border-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 shadow-md hover:shadow-lg'
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]}`}
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
    >
      {direction === 'left' ? (
        <ChevronLeft className="w-5 h-5" />
      ) : (
        <ChevronRight className="w-5 h-5" />
      )}
    </motion.button>
  );
};

const LoadingState = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="relative">
        <motion.div
          className="w-12 h-12 border-3 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-400 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <div className="absolute inset-0 rounded-full bg-blue-50/50 dark:bg-blue-500/20"></div>
      </div>
      <p className="mt-6 text-gray-700 dark:text-gray-300 font-medium">Cargando contenido...</p>
    </motion.div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <motion.div
      className="backdrop-blur-xl bg-white/80 dark:bg-white/5 rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-100 dark:border-white/10"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="text-center">
        <div className="bg-red-50 dark:bg-red-500/20 w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Error al cargar contenido
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">{message}</p>
        <motion.button
          onClick={onRetry}
          className="px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 font-medium transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Intentar nuevamente
        </motion.button>
      </div>
    </motion.div>
  </div>
);

// Enhanced Trust Indicators
const TrustIndicators = () => (
  <motion.div
    className="w-full px-4"
    initial={{ opacity: 0, y: 15 }}
    animate={{ opacity: 0.9, y: 0 }}
    transition={{ duration: 0.5, delay: 0.8 }}
  >
    {/* Mobile: Only Icons */}
    <div className="flex sm:hidden items-center justify-center gap-4 max-w-xs mx-auto">
      <div className="flex items-center justify-center w-12 h-12 text-white/90 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
        <Shield className="w-6 h-6" />
      </div>
      <div className="flex items-center justify-center w-12 h-12 text-white/90 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
        <Award className="w-6 h-6" />
      </div>
      <div className="flex items-center justify-center w-12 h-12 text-white/90 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg">
        <Users className="w-6 h-6" />
      </div>
    </div>
    
    {/* Desktop: Horizontal layout with text */}
    <div className="hidden sm:flex flex-wrap items-center justify-center gap-6 opacity-90">
      <div className="flex items-center gap-2 text-white/80 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20">
        <Shield className="w-4 h-4" />
        <span className="text-sm font-medium">Transacciones Seguras</span>
      </div>
      <div className="flex items-center gap-2 text-white/80 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20">
        <Award className="w-4 h-4" />
        <span className="text-sm font-medium">Agentes Certificados</span>
      </div>
      <div className="flex items-center gap-2 text-white/80 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-xl border border-white/20">
        <Users className="w-4 h-4" />
        <span className="text-sm font-medium">Usuarios Verificados</span>
      </div>
    </div>
  </motion.div>
);

// Enhanced Property Section
const PropertySection = ({ 
  title, 
  subtitle, 
  icon, 
  properties, 
  currentPage, 
  onPrevPage, 
  onNextPage, 
  onFavoriteUpdate, 
  propertyType,
  featured = false 
}) => {
  const [itemsPerPage, setItemsPerPage] = useState(3);
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setItemsPerPage(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(3);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const startIndex = currentPage * itemsPerPage;
  const currentProperties = properties.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(properties.length / itemsPerPage);

  if (properties.length === 0) return null;

  return (
    <motion.section
      className={`py-12 sm:py-16 ${featured ? 'bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/20 dark:to-indigo-900/20 border-y border-blue-100/50 dark:border-blue-800/30' : 'bg-white dark:bg-gray-900'}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={animations.staggerContainer}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ProfessionalSectionTitle
          icon={icon}
          title={title}
          subtitle={subtitle}
          accent={featured}
        />

        <div className="relative">
          {/* Desktop Navigation */}
          {totalPages > 1 && (
            <>
              <div className="absolute top-1/2 -left-4 -translate-y-1/2 hidden lg:block z-10">
                <NavigationButton
                  onClick={onPrevPage}
                  disabled={currentPage === 0}
                  direction="left"
                  variant={featured ? 'primary' : 'default'}
                />
              </div>
              
              <div className="absolute top-1/2 -right-4 -translate-y-1/2 hidden lg:block z-10">
                <NavigationButton
                  onClick={onNextPage}
                  disabled={currentPage >= totalPages - 1}
                  direction="right"
                  variant={featured ? 'primary' : 'default'}
                />
              </div>
            </>
          )}

          {/* Properties Grid */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={animations.staggerContainer}
          >
            {currentProperties.map((property) => (
              <motion.div
                key={property.id}
                variants={animations.fadeInUp}
                initial="rest"
                whileHover="hover"
                animate="rest"
                className="mx-auto w-full max-w-md"
              >
                <motion.div 
                  variants={animations.cardHover}
                  className="h-full"
                >
                  <PropertyCard
                    property={property}
                    onFavoriteUpdate={onFavoriteUpdate}
                  />
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mobile Navigation */}
        {totalPages > 1 && (
          <div className="mt-8 sm:mt-12 flex items-center justify-center gap-3 sm:gap-4 lg:hidden px-4">
            <NavigationButton
              onClick={onPrevPage}
              disabled={currentPage === 0}
              direction="left"
              variant={featured ? 'primary' : 'default'}
            />
            <div className="flex items-center gap-2 backdrop-blur-xl bg-white/80 dark:bg-white/10 px-3 py-2 rounded-xl shadow-sm border border-gray-200 dark:border-white/20">
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Página</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100 min-w-[16px] text-center text-sm">
                {currentPage + 1}
              </span>
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">de {totalPages}</span>
            </div>
            <NavigationButton
              onClick={onNextPage}
              disabled={currentPage >= totalPages - 1}
              direction="right"
              variant={featured ? 'primary' : 'default'}
            />
          </div>
        )}

        {/* View All Button */}
        {propertyType !== 'todos' && propertyTypeMap[propertyType] && (
          <motion.div
            className="mt-8 sm:mt-12 text-center px-4"
            variants={animations.fadeInUp}
          >
            <Link
              to={`/${propertyTypeMap[propertyType].route}`}
              className="inline-flex items-center group gap-2 sm:gap-3 px-6 sm:px-8 py-3 backdrop-blur-xl bg-white/80 dark:bg-white/10 text-gray-700 dark:text-gray-200 font-medium rounded-xl border-2 border-gray-200 dark:border-white/20 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/20 hover:text-blue-700 dark:hover:text-blue-400 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm sm:text-base"
            >
              <span>Ver todas las {propertyTypeMap[propertyType].label}</span>
              <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        )}
      </div>
    </motion.section>
  );
};

// Main Component
const Home = () => {
  const { user, isAuthenticated, fetchWithAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { searchPropiedades, searchAlquileres, searchProyectos } = usePropiedades();
  
  // Estados principales
  const [properties, setProperties] = useState({
    casa: [],
    departamento: [],
    terreno: [],
    local: [],
    recommended: []
  });
  const [recommendedProperties, setRecommendedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState({
    casa: 0,
    departamento: 0,
    terreno: 0,
    local: 0,
    recommended: 0
  });
  const [error, setError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [currentPartnerPage, setCurrentPartnerPage] = useState(0);

  // Estados del SearchBar integrado
  const [activeTab, setActiveTab] = useState(() => {
    const operacion = searchParams.get('operacion');
    if (operacion === 'alquilar') return 'alquilar';
    if (operacion === 'proyectos') return 'proyectos';
    return 'comprar';
  });
  
  const [tipo, setTipo] = useState(() => {
    const tipoParam = searchParams.get('tipo');
    return tipoParam || 'todos';
  });
  
  const [estadoProyecto, setEstadoProyecto] = useState(() => {
    const estadoParam = searchParams.get('estado_proyecto');
    return estadoParam || 'todos';
  });
  
  const [searchTitle, setSearchTitle] = useState(() => searchParams.get('tituloSearch') || '');
  const [precioMin, setPrecioMin] = useState(() => searchParams.get('precioMin') || '');
  const [precioMax, setPrecioMax] = useState(() => searchParams.get('precioMax') || '');
  const [ubicacion, setUbicacion] = useState(() => searchParams.get('ubicacion') || '');
  const [dormitorios, setDormitorios] = useState(() => searchParams.get('dormitorios') || '');
  
  const [showFilters, setShowFilters] = useState(false);
  const [showPropertyTypes, setShowPropertyTypes] = useState(false);
  const [showProjectStages, setShowProjectStages] = useState(false);
  
  // Estado para almacenar datos de ubicación seleccionada
  const [ubicacionData, setUbicacionData] = useState(null);
  
  // Referencias para controlar clicks fuera del dropdown
  const propertyTypeRef = useRef(null);
  const projectStageRef = useRef(null);

  // Memoized particles for stable animation
  const particles = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => ({
      id: i,
      size: Math.random() * 200 + 100,
      x: Math.random() * 100,
      y: Math.random() * 100,
      duration: Math.random() * 15 + 20,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.08 + 0.02
    }));
  }, []);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (propertyTypeRef.current && !propertyTypeRef.current.contains(event.target)) {
        setShowPropertyTypes(false);
      }
      if (projectStageRef.current && !projectStageRef.current.contains(event.target)) {
        setShowProjectStages(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Data fetching functions
  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      
      if (isAuthenticated) {
        response = await fetchWithAuth('https://backend-production-1786.up.railway.app/api/propiedades');
      } else {
        response = await fetch('https://backend-production-1786.up.railway.app/api/propiedades', {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error al cargar las propiedades (${response.status})`);
        }
      }

      if (!response) return;

      const data = await response.json();

      const organizedProperties = {
        casa: data.filter(prop => prop.tipo === 'casa')
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 9),
        departamento: data.filter(prop => prop.tipo === 'departamento')
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 9),
        terreno: data.filter(prop => prop.tipo === 'terreno')
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 9),
        local: data.filter(prop => prop.tipo === 'local')
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 9),
        recommended: data.filter(prop => prop.recomendado === true)
          .sort((a, b) => new Date(b.fecha_publicacion) - new Date(a.fecha_publicacion))
          .slice(0, 9)
      };

      setProperties(organizedProperties);
    } catch (error) {
      console.error('Error fetching properties:', error);
      
      if (error.message !== 'Sesión expirada') {
        setError('No pudimos cargar las propiedades. Por favor, intenta de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendedProperties = async () => {
    try {
      const response = await fetchWithAuth('https://backend-production-1786.up.railway.app/api/favoritos');
      
      if (!response) return;

      const favorites = await response.json();
      setRecommendedProperties(favorites);
    } catch (error) {
      console.error('Error fetching recommended properties:', error);
      
      if (error.message !== 'Sesión expirada') {
        console.error('Error al cargar recomendaciones:', error.message);
      }
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores?tipo=constructora', {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error al obtener los socios constructoras: ${response.status}`);
      }

      const data = await response.json();

      const activePartners = data.resultados ? data.resultados.filter(partner =>
        partner.estado === 'activo' && partner.foto_perfil
      ).map(partner => ({
        ...partner,
        foto_perfil: partner.foto_perfil
          ? `${CLOUDFLARE_PROFILES_URL}/${partner.foto_perfil
              .replace('http://localhost:3001/profiles/', '')
              .replace('/profiles/', '')
              .replace('profiles/', '')
              .replace(/^\//, '')}`
          : '/default-profile.png'
      })) : [];

      setPartners(activePartners);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Search handlers
  const handleSearch = async (params) => {
    const searchParams = {
      operacion: params.operacion || activeTab,
      tipo: params.tipo || (tipo !== 'todos' ? tipo : ''),
      tituloSearch: params.tituloSearch || searchTitle.trim(),
      precioMin: params.precioMin || precioMin,
      precioMax: params.precioMax || precioMax,
      ubicacion: params.ubicacion || ubicacion
    };

    // Añadir parámetros específicos para proyectos
    if (activeTab === 'proyectos') {
      searchParams.tipo_proyecto = searchParams.tipo;
      searchParams.estado_proyecto = params.estado_proyecto || (estadoProyecto !== 'todos' ? estadoProyecto : '');
      searchParams.dormitorios = params.dormitorios || dormitorios;
      delete searchParams.tipo;
    }

    // Añadir datos específicos de ubicación si están disponibles
    if (ubicacionData) {
      if (ubicacionData.type === LocationType.CITY) {
        searchParams.ciudad = ubicacionData.value;
        searchParams.pais = ubicacionData.extraData?.countryCode;
        
        if (ubicacionData.extraData?.latitude && ubicacionData.extraData?.longitude) {
          searchParams.lat = ubicacionData.extraData.latitude;
          searchParams.lng = ubicacionData.extraData.longitude;
        }
      }
    }

    try {
      if (searchParams.operacion === 'alquilar') {
        await searchAlquileres(searchParams);
      } else if (searchParams.operacion === 'proyectos') {
        await searchProyectos(searchParams);
      } else {
        await searchPropiedades(searchParams);
      }
    } catch (error) {
      console.error("Error al realizar la búsqueda:", error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const searchParams = {
      operacion: activeTab,
      tituloSearch: searchTitle.trim(),
      precioMin: precioMin || '',
      precioMax: precioMax || '',
      ubicacion: ubicacion || ''
    };

    if (activeTab === 'proyectos') {
      searchParams.tipo_proyecto = tipo !== 'todos' ? tipo : '';
      searchParams.estado_proyecto = estadoProyecto !== 'todos' ? estadoProyecto : '';
      searchParams.dormitorios = dormitorios;
    } else {
      searchParams.tipo = tipo !== 'todos' ? tipo : '';
    }

    if (ubicacionData) {
      if (ubicacionData.type === LocationType.CITY) {
        searchParams.ciudad = ubicacionData.value;
        searchParams.pais = ubicacionData.extraData?.countryCode;
      }
    }

    handleSearch(searchParams);

    const queryParams = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value);
      }
    });
    
    navigate(`/buscar?${queryParams.toString()}`);
  };

  const handleReset = () => {
    setSearchTitle('');
    setPrecioMin('');
    setPrecioMax('');
    setUbicacion('');
    setUbicacionData(null);
    setTipo('todos');
    setEstadoProyecto('todos');
    setDormitorios('');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTipo('todos');
    setEstadoProyecto('todos');
  };

  const handleTipoChange = (newTipo) => {
    setTipo(newTipo);
    setShowPropertyTypes(false);
  };

  const handleEstadoProyectoChange = (newEstado) => {
    setEstadoProyecto(newEstado);
    setShowProjectStages(false);
  };

  // Event handlers
  const handleFavoriteUpdate = (propertyId, isFavorite) => {
    setProperties(prevProperties => {
      const newProperties = { ...prevProperties };

      Object.keys(newProperties).forEach(category => {
        newProperties[category] = newProperties[category].map(prop =>
          prop.id === propertyId ? { ...prop, isFavorite } : prop
        );
      });

      return newProperties;
    });
  };

  const handlePrevPage = (type) => {
    setCurrentPage(prev => ({
      ...prev,
      [type]: Math.max(prev[type] - 1, 0)
    }));
  };

  const handleNextPage = (type) => {
    const itemsPerPage = window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;
    setCurrentPage(prev => ({
      ...prev,
      [type]: Math.min(
        prev[type] + 1,
        Math.ceil(properties[type]?.length / itemsPerPage) - 1
      )
    }));
  };

  // Effects
  useEffect(() => {
    fetchProperties();
    fetchPartners();
    
    if (isAuthenticated) {
      fetchRecommendedProperties();
    }
  }, [isAuthenticated]);

  // Loading and error states
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={fetchProperties} />;

  // Property sections configuration
  const sections = [
    {
      id: 'featured',
      title: "Propiedades Destacadas",
      subtitle: "Propiedades premium seleccionadas por nuestro equipo de expertos inmobiliarios",
      icon: Star,
      properties: properties.recommended,
      currentPage: currentPage.recommended,
      propertyType: "todos",
      show: properties.recommended.length > 0,
      featured: true
    },
    {
      id: 'houses',
      title: "Casas",
      subtitle: "Descubre casas familiares en las mejores ubicaciones de la ciudad",
      icon: HomeIcon,
      properties: properties.casa,
      currentPage: currentPage.casa,
      propertyType: "casa"
    },
    {
      id: 'apartments',
      title: "Departamentos",
      subtitle: "Apartamentos con diseño contemporáneo y amenidades de primera clase",
      icon: Building,
      properties: properties.departamento,
      currentPage: currentPage.departamento,
      propertyType: "departamento"
    },
    {
      id: 'lands',
      title: "Terrenos",
      subtitle: "Oportunidades de inversión en terrenos con alto potencial de valorización",
      icon: Map,
      properties: properties.terreno,
      currentPage: currentPage.terreno,
      propertyType: "terreno"
    },
    {
      id: 'commercial',
      title: "Locales",
      subtitle: "Locales estratégicamente ubicados para el crecimiento de tu negocio",
      icon: Store,
      properties: properties.local,
      currentPage: currentPage.local,
      propertyType: "local"
    }
  ];

  const currentPropertyTypes = activeTab === 'proyectos' ? PROJECT_TYPES : PROPERTY_TYPES;

  return (
    <AnimatePresence>
      <div className="min-h-screen">
        {/* Enhanced Hero Section con SearchBar integrado */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pb-8 sm:pb-16">
          {/* Enhanced Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
            {/* Stable Animated Particles */}
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="absolute rounded-full bg-gradient-to-br from-blue-400/8 to-purple-400/8 blur-3xl"
                style={{
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  opacity: particle.opacity,
                }}
                animate={{
                  x: [0, 20, -15, 0],
                  y: [0, -20, 15, 0],
                  scale: [1, 1.1, 0.9, 1],
                }}
                transition={{
                  duration: particle.duration,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: particle.delay,
                }}
              />
            ))}
            
            {/* Enhanced Grid Pattern */}
            <div 
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px'
              }}
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10" />
          </div>

          {/* Hero Content con SearchBar integrado */}
          <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10 pt-8 sm:pt-16 md:pt-16 lg:pt-10">
            <motion.div
              className="flex flex-col items-center justify-center space-y-6 sm:space-y-8 md:space-y-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                className="space-y-4 sm:space-y-5 md:space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {/* Título más pequeño */}
                <h1 className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-7xl font-bold text-white tracking-tight leading-tight px-2">
                  Encuentra tu{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">
                    Hogar Ideal
                  </span>
                </h1>
                
                <p className="text-lg sm:text-xl md:text-2xl lg:text-2xl text-white/90 max-w-4xl mx-auto font-light leading-relaxed px-4">
                  Publica y encuentra de forma inteligente e innovadora tu próximo hogar.
                </p>
              </motion.div>
              
              {/* SearchBar integrado */}
              <motion.div
                className="w-full max-w-4xl mx-auto mt-2 sm:mt-3 px-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                style={{ zIndex: 999 }}
              >
                {/* Enhanced Main Search Container */}
                <motion.div 
                  className="bg-white/10 dark:bg-white/5 backdrop-blur-xl rounded-2xl shadow-xl dark:shadow-black/50 border border-white/20 dark:border-white/10 relative"
                  initial={{ opacity: 0, y: 20, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                >
                  <form onSubmit={handleSubmit}>
                    {/* Enhanced Header with Tabs */}
                    <div className="relative overflow-hidden rounded-t-2xl">
                      {/* Decorative background pattern */}
                      <div className="absolute inset-0">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-blue-600/10" />
                        <div className="absolute top-0 left-1/4 w-24 h-24 bg-blue-400/10 rounded-full blur-2xl" />
                        <div className="absolute top-0 right-1/4 w-24 h-24 bg-purple-400/10 rounded-full blur-2xl" />
                      </div>
                      
                      <div className="relative flex justify-center p-3 sm:p-5 pb-2 sm:pb-3">
                        <div className="flex gap-1 sm:gap-1.5 p-1 bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/20 dark:border-white/10">
                          <TabButton
                            active={activeTab === 'comprar'}
                            onClick={() => handleTabChange('comprar')}
                            icon={HomeIcon}
                          >
                            Comprar
                          </TabButton>
                          <TabButton
                            active={activeTab === 'alquilar'}
                            onClick={() => handleTabChange('alquilar')}
                            icon={Building2}
                          >
                            Alquilar
                          </TabButton>
                          <TabButton
                            active={activeTab === 'proyectos'}
                            onClick={() => handleTabChange('proyectos')}
                            icon={TrendingUp}
                          >
                            Proyectos
                          </TabButton>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Search Section */}
                    <div className="p-3 sm:p-5 pt-1 sm:pt-2 relative overflow-visible">
                      {/* Enhanced Search Input */}
                      <div className="mb-3 sm:mb-4">
                        <div className="relative group">
                          <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-white/60 dark:text-gray-400 w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 group-focus-within:text-blue-400 group-focus-within:scale-105" />
                          <input
                            type="text"
                            placeholder={activeTab === 'proyectos' ? "Buscar proyectos..." : "Buscar propiedades..."}
                            value={searchTitle}
                            onChange={(e) => setSearchTitle(e.target.value)}
                            maxLength={50}
                            className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 placeholder-white/50 dark:placeholder-gray-400 text-sm sm:text-base
                                    border border-white/20 dark:border-white/10 focus:outline-none focus:ring-3 focus:ring-blue-500/30
                                    focus:border-blue-400/50 dark:focus:border-blue-400/50 transition-all duration-300 font-medium"
                          />
                          <div className="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 
                                          opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
                          <Sparkles className="hidden sm:block absolute right-4 top-1/2 transform -translate-y-1/2 text-white/30 dark:text-gray-500 w-4 h-4 opacity-0 group-focus-within:opacity-100 transition-all duration-300" />
                        </div>
                      </div>

                      {/* Enhanced Bottom Row */}
                      <div className="flex flex-col lg:flex-row gap-2 sm:gap-3 relative overflow-visible"
                           style={{ zIndex: 1000 }}>
                        {/* Enhanced Property Type Selection */}
                        <div className="flex-1 relative overflow-visible" ref={propertyTypeRef} style={{ zIndex: 1000 }}>
                          <motion.div
                            className="group flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm 
                                      border border-white/20 dark:border-white/10 cursor-pointer text-white dark:text-gray-200 transition-all duration-300
                                      hover:bg-white/15 dark:hover:bg-white/10 hover:border-white/30 dark:hover:border-white/20"
                            onClick={() => setShowPropertyTypes(!showPropertyTypes)}
                            whileHover={{ scale: 1.005, y: -0.5 }}
                            whileTap={{ scale: 0.995 }}
                          >
                            {(() => {
                              const Icon = currentPropertyTypes.find(t => t.value === tipo)?.icon;
                              return Icon ? <Icon className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-white/80 dark:text-gray-300 transition-transform duration-300 group-hover:scale-105" /> : null;
                            })()}
                            <span className="font-medium text-left text-sm sm:text-base flex-1">{currentPropertyTypes.find(t => t.value === tipo)?.label}</span>
                            <ChevronDown className={`w-4 h-4 transition-all duration-300 ${
                              showPropertyTypes ? 'rotate-180 text-blue-300 dark:text-blue-400' : 'group-hover:text-blue-300 dark:group-hover:text-blue-400'
                            }`} />
                          </motion.div>

                          <AnimatePresence>
                            {showPropertyTypes && (
                              <motion.div 
                                className="absolute left-0 right-0 top-full mt-2 p-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-xl shadow-2xl dark:shadow-black/50
                                          grid grid-cols-1 sm:grid-cols-2 gap-2 border border-white/20 dark:border-gray-600
                                          max-h-60 overflow-y-auto"
                                style={{ zIndex: 10000 }}
                                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                              >
                                {currentPropertyTypes.map((type) => (
                                  <PropertyTypeButton
                                    key={type.value}
                                    icon={type.icon}
                                    label={type.label}
                                    selected={tipo === type.value}
                                    onClick={() => handleTipoChange(type.value)}
                                  />
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        {/* Enhanced Project Stage Selection */}
                        {activeTab === 'proyectos' && (
                          <div className="flex-1 relative overflow-visible" ref={projectStageRef} style={{ zIndex: 1000 }}>
                            <motion.div
                              className="group flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm 
                                        border border-white/20 dark:border-white/10 cursor-pointer text-white dark:text-gray-200 transition-all duration-300
                                        hover:bg-white/15 dark:hover:bg-white/10 hover:border-white/30 dark:hover:border-white/20"
                              onClick={() => setShowProjectStages(!showProjectStages)}
                              whileHover={{ scale: 1.005, y: -0.5 }}
                              whileTap={{ scale: 0.995 }}
                            >
                              <Castle className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-white/80 dark:text-gray-300 transition-transform duration-300 group-hover:scale-105" />
                              <span className="font-medium text-left text-sm sm:text-base flex-1">{PROJECT_STAGES.find(s => s.value === estadoProyecto)?.label}</span>
                              <ChevronDown className={`w-4 h-4 transition-all duration-300 ${
                                showProjectStages ? 'rotate-180 text-blue-300 dark:text-blue-400' : 'group-hover:text-blue-300 dark:group-hover:text-blue-400'
                              }`} />
                            </motion.div>

                            <AnimatePresence>
                              {showProjectStages && (
                                <motion.div 
                                  className="absolute left-0 right-0 top-full mt-2 p-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-xl shadow-2xl dark:shadow-black/50
                                            space-y-1.5 border border-white/20 dark:border-gray-600
                                            max-h-60 overflow-y-auto"
                                  style={{ zIndex: 10000 }}
                                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                  transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                                >
                                  {PROJECT_STAGES.map((stage) => (
                                    <motion.button
                                      key={stage.value}
                                      onClick={() => handleEstadoProyectoChange(stage.value)}
                                      className={`group relative overflow-hidden w-full flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300
                                                ${estadoProyecto === stage.value 
                                        ? 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white shadow-md' 
                                        : 'bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700'}`}
                                      whileHover={{ scale: 1.01, y: -0.5 }}
                                      whileTap={{ scale: 0.99 }}
                                    >
                                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                                                      translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                                      <span className="font-medium relative z-10 text-sm">{stage.label}</span>
                                      {estadoProyecto === stage.value && <Star className="hidden sm:block w-3 h-3 text-white/70 ml-auto relative z-10" />}
                                    </motion.button>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )}

                        {/* Enhanced Action Buttons */}
                        <div className="flex gap-2 sm:gap-2.5">
                          <motion.button
                            type="button"
                            onClick={() => setShowFilters(!showFilters)}
                            className={`group relative overflow-hidden flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-sm sm:text-base
                                     transition-all duration-300 flex-1 lg:flex-none ${
                              showFilters 
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-md' 
                                : 'bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 border border-white/20 dark:border-white/10 hover:bg-white/15 dark:hover:bg-white/10'
                            }`}
                            whileHover={{ scale: 1.01, y: -0.5 }}
                            whileTap={{ scale: 0.99 }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                                            translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                            <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300 group-hover:rotate-12 relative z-10" />
                            <span className="relative z-10">Filtros</span>
                          </motion.button>

                          <motion.button
                            type="submit"
                            className="group relative overflow-hidden flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 
                                     text-white rounded-lg sm:rounded-xl shadow-lg shadow-blue-500/25 dark:shadow-blue-500/30 font-medium text-sm sm:text-base flex-1 lg:flex-none"
                            whileHover={{ 
                              scale: 1.01, 
                              y: -1,
                              boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)" 
                            }}
                            whileTap={{ scale: 0.99 }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                                            translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                            <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300 group-hover:scale-105 relative z-10" />
                            <span className="relative z-10">Buscar</span>
                          </motion.button>
                        </div>
                      </div>

                      {/* Enhanced Filters Section */}
                      <AnimatePresence>
                        {showFilters && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                            className="mt-4"
                          >
                            <div className="p-3 sm:p-4 bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/20 dark:border-white/10">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                {/* Enhanced Location Search */}
                                <div className="relative" style={{ zIndex: 1000 }}>
                                  <label className="block text-xs font-medium text-white/90 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                                    <MapPin className="hidden sm:block w-3.5 h-3.5" />
                                    Ubicación
                                  </label>
                                  <div className="relative">
                                    <EnhancedLocationSearch
                                      value={ubicacion}
                                      onChange={(value) => setUbicacion(value)}
                                      onSelect={(locationData) => setUbicacionData(locationData)}
                                    />
                                  </div>
                                </div>

                                {/* Enhanced Price Inputs */}
                                <FilterInput
                                  label={`Precio ${activeTab === 'alquilar' ? 'mensual' : activeTab === 'proyectos' ? 'desde' : ''} mínimo (S/)`}
                                  value={precioMin}
                                  onChange={(e) => setPrecioMin(e.target.value)}
                                  placeholder="Mínimo"
                                  type="number"
                                  min="0"
                                  icon={TrendingUp}
                                />

                                <FilterInput
                                  label={`Precio ${activeTab === 'alquilar' ? 'mensual' : activeTab === 'proyectos' ? 'desde' : ''} máximo (S/)`}
                                  value={precioMax}
                                  onChange={(e) => setPrecioMax(e.target.value)}
                                  placeholder="Máximo"
                                  type="number"
                                  min="0"
                                  icon={TrendingUp}
                                />

                                {/* Enhanced Dormitorios for projects */}
                                {activeTab === 'proyectos' && (
                                  <div className="space-y-2">
                                    <label className="block text-xs font-medium text-white/90 dark:text-gray-300 flex items-center gap-1.5">
                                      <HomeIcon className="hidden sm:block w-3.5 h-3.5" />
                                      Dormitorios
                                    </label>
                                    <div className="relative">
                                      <select
                                        value={dormitorios}
                                        onChange={(e) => setDormitorios(e.target.value)}
                                        className="w-full px-3 sm:px-3.5 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 appearance-none text-sm
                                                 border border-white/20 dark:border-white/10 focus:outline-none focus:ring-3 focus:ring-blue-500/30 
                                                 focus:border-blue-400/50 dark:focus:border-blue-400/50 transition-all duration-300 font-medium
                                                 [&>option]:bg-gray-800 [&>option]:text-gray-100"
                                      >
                                        <option value="" className="bg-gray-800 text-gray-100">Todos</option>
                                        <option value="1" className="bg-gray-800 text-gray-100">1 Dormitorio</option>
                                        <option value="2" className="bg-gray-800 text-gray-100">2 Dormitorios</option>
                                        <option value="3" className="bg-gray-800 text-gray-100">3 Dormitorios</option>
                                        <option value="4" className="bg-gray-800 text-gray-100">4+ Dormitorios</option>
                                      </select>
                                      <ChevronDown className="absolute right-3 sm:right-3.5 top-1/2 transform -translate-y-1/2 text-white/60 dark:text-gray-400 w-4 h-4 pointer-events-none" />
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Enhanced Filter Actions */}
                              <div className="flex justify-end gap-2 sm:gap-3 mt-3 sm:mt-4 pt-3 border-t border-white/20 dark:border-white/10">
                                <motion.button
                                  type="button"
                                  onClick={handleReset}
                                  className="group flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 rounded-lg sm:rounded-xl
                                           border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300 font-medium text-xs sm:text-sm"
                                  whileHover={{ scale: 1.01, y: -0.5 }}
                                  whileTap={{ scale: 0.99 }}
                                >
                                  <X className="w-3 sm:w-3.5 h-3 sm:h-3.5 transition-transform duration-300 group-hover:rotate-90" />
                                  <span>Limpiar</span>
                                </motion.button>
                                <motion.button
                                  type="submit"
                                  className="group flex items-center gap-1.5 px-4 sm:px-5 py-2 bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 rounded-lg sm:rounded-xl
                                           shadow-md font-medium hover:shadow-lg transition-all duration-300 text-xs sm:text-sm"
                                  whileHover={{ scale: 1.01, y: -0.5 }}
                                  whileTap={{ scale: 0.99 }}
                                >
                                  <Check className="w-3 sm:w-3.5 h-3 sm:h-3.5 transition-transform duration-300 group-hover:scale-105" />
                                  <span>Aplicar</span>
                                  <ArrowRight className="w-3 sm:w-3.5 h-3 sm:h-3.5 ml-0.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                                </motion.button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
              
              <TrustIndicators />

              {/* Balanced spacing for dropdowns */}
              <div className="h-8 sm:h-10 md:h-12"></div>
            </motion.div>
          </div>
        </section>

        {/* Enhanced Stats Section */}
        <motion.section 
          className="py-12 sm:py-16 bg-white dark:bg-gray-900"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-500/20 rounded-2xl mb-2 sm:mb-3">
                  <Target className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">1,200+</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Propiedades Activas</div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-green-100 dark:bg-green-500/20 rounded-2xl mb-2 sm:mb-3">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">800+</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Clientes Satisfechos</div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 dark:bg-purple-500/20 rounded-2xl mb-2 sm:mb-3">
                  <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">50+</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Ciudades</div>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 dark:bg-amber-500/20 rounded-2xl mb-2 sm:mb-3">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">95%</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Satisfacción</div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Property Sections */}
        <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
          {sections.map(section => (
            (section.show === undefined || section.show) && (
              <PropertySection
                key={section.id}
                title={section.title}
                subtitle={section.subtitle}
                icon={section.icon}
                properties={section.properties}
                currentPage={section.currentPage}
                onPrevPage={() => handlePrevPage(section.propertyType)}
                onNextPage={() => handleNextPage(section.propertyType)}
                onFavoriteUpdate={handleFavoriteUpdate}
                propertyType={section.propertyType}
                featured={section.featured}
              />
            )
          ))}
          
          {/* Enhanced Partners Section */}
          {partners.length > 0 && (
            <motion.section
              className="py-12 sm:py-16 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-gray-800 dark:to-gray-900"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={animations.staggerContainer}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <ProfessionalSectionTitle
                  icon={Building}
                  title="Socios Estratégicos"
                  subtitle="Colaboramos con las constructoras más prestigiosas para ofrecerte las mejores oportunidades"
                  accent={true}
                />
                
                <div className="relative">
                  {Math.ceil(partners.length / 3) > 1 && (
                    <>
                      <div className="absolute top-1/2 -left-4 -translate-y-1/2 hidden lg:block z-10">
                        <NavigationButton
                          onClick={() => setCurrentPartnerPage(prev => Math.max(0, prev - 1))}
                          disabled={currentPartnerPage === 0}
                          direction="left"
                          variant="primary"
                        />
                      </div>
                      
                      <div className="absolute top-1/2 -right-4 -translate-y-1/2 hidden lg:block z-10">
                        <NavigationButton
                          onClick={() => setCurrentPartnerPage(prev =>
                            Math.min(prev + 1, Math.ceil(partners.length / 3) - 1)
                          )}
                          disabled={currentPartnerPage >= Math.ceil(partners.length / 3) - 1}
                          direction="right"
                          variant="primary"
                        />
                      </div>
                    </>
                  )}
                  
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
                    variants={animations.staggerContainer}
                  >
                    {partners
                      .slice(currentPartnerPage * 3, (currentPartnerPage + 1) * 3)
                      .map((partner) => (
                        <motion.div
                          key={partner.id}
                          variants={animations.fadeInUp}
                          initial="rest"
                          whileHover="hover"
                          animate="rest"
                          className="mx-auto w-full max-w-md"
                        >
                          <motion.div 
                            variants={animations.cardHover}
                            className="h-full"
                          >
                            <PartnerCard partner={partner} />
                          </motion.div>
                        </motion.div>
                      ))}
                  </motion.div>
                </div>
                
                {/* Mobile Navigation for Partners */}
                {Math.ceil(partners.length / 3) > 1 && (
                  <div className="mt-8 sm:mt-12 flex items-center justify-center gap-3 sm:gap-4 lg:hidden px-4">
                    <NavigationButton
                      onClick={() => setCurrentPartnerPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPartnerPage === 0}
                      direction="left"
                      variant="primary"
                    />
                    <div className="flex items-center gap-2 backdrop-blur-xl bg-white/80 dark:bg-white/10 px-3 py-2 rounded-xl shadow-sm border border-gray-200 dark:border-white/20">
                      <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Página</span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100 min-w-[16px] text-center text-sm">
                        {currentPartnerPage + 1}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">de {Math.ceil(partners.length / 3)}</span>
                    </div>
                    <NavigationButton
                      onClick={() => setCurrentPartnerPage(prev =>
                        Math.min(prev + 1, Math.ceil(partners.length / 3) - 1)
                      )}
                      disabled={currentPartnerPage >= Math.ceil(partners.length / 3) - 1}
                      direction="right"
                      variant="primary"
                    />
                  </div>
                )}
              </div>
            </motion.section>
          )}
          
          {/* Personal Recommendations */}
          {recommendedProperties.length > 0 && (
            <PropertySection
              title="Recomendaciones Personales"
              subtitle="Propiedades cuidadosamente seleccionadas basadas en tus preferencias y búsquedas anteriores"
              icon={TrendingUp}
              properties={recommendedProperties}
              currentPage={currentPage.recommended}
              onPrevPage={() => handlePrevPage('recommended')}
              onNextPage={() => handleNextPage('recommended')}
              onFavoriteUpdate={handleFavoriteUpdate}
              propertyType="todos"
              featured={true}
            />
          )}
        </div>
      </div>
    </AnimatePresence>
  );
};

export default Home;